import React, { useState, useEffect, useRef, useMemo } from 'react';
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import * as FieldTypes from '../Shared/FieldTypes/FieldTypes.js'
import * as LayoutComponents from '../Shared/LayoutComponents.js'
import ProductTreeSelector from "./ProductTreeSelector.js";
import GenericDataTable from "../Shared/DataTable/GenericDataTable.js";
import SubmissionsAddItemPopup from "../Submissions/AddItemPopup.js";
import '../Shared/Style.css';
import "./App.css";

import "trumbowyg/dist/ui/trumbowyg.min.css";

const ItemDetailsPane = ({ globalContext }) => {

    const AppContext = globalContext;
    const [isSavingItem, setIsSavingItem] = useState(false);
    const [isShowingSaveMessage, setIsShowingSaveMessage] = useState(false);
    const [isShowingConfirmSavePopup, setIsShowingConfirmSavePopup] = useState(false);
    const [visibleDetailsTab, setVisibleDetailsTab] = useState("change-assessment");
    const [itemDetailsPaneActive, setItemDetailsPaneActive] = useState(AppContext.isPaneOpen);
    const [itemDetailsPaneOpenEffect, setItemDetailsPaneOpenEffect] = useState(false);
    const [showItemContextMenu, setShowItemContextMenu] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [version, setVersion] = useState(0);
    const [itemListRefresh, setItemListRefresh] = useState(0);
    const [productIds, setProductIds] = useState([]);
    const [isShowingAddProductsPopup, setIsShowingAddProductsPopup] = useState(false);
    const [isShowingConfirmAddProductsPopup, setIsShowingConfirmAddProductsPopup] = useState(false);
    const [isPreviewingAddProducts, setIsPreviewingAddProducts] = useState(false);
    const [isAddingProducts, setIsAddingProducts] = useState(false);
    const [errorAddingProducts, setErrorAddingProducts] = useState(null);
    const [addProductsPreview, setAddProductsPreview] = useState(null);
    const [addProductsPopupKey, setAddProductsPopupKey] = useState(0);

    const [errorEditTitle, setErrorEditTitle] = useState(false);
    const [errorEditChangeAssessmentType, setErrorEditChangeAssessmentType] = useState(false);
    const [errorEditDueDate, setErrorEditDueDate] = useState(false);
    const [errorEditingItem, setErrorEditingItem] = useState(null);

    const [isEditingField, setIsEditingField] = useState({
        Title: false,
        Description: false,
        Type: false,
        DueDate: false,
        Owner: false
    });

    const contextMenuButton = useRef(null);
    const contextMenu = useRef(null);

    // Reinitialize state when pane opens
    const reinitialize = () => {
        changeTab("change-assessment");
        setIsSavingItem(false);
        AppContext.setIsShowingDeleteConfirmation(false);
        setIsShowingSaveMessage(false);
        setIsEditMode(false);
    }

    useEffect(() => {
        if (itemDetailsPaneActive)
            reinitialize();
    }, [itemDetailsPaneActive]);

    useEffect(() => {
        if (!itemDetailsPaneOpenEffect)
            setShowItemContextMenu(false);
    }, [itemDetailsPaneOpenEffect])

    // Pane Open/Close Animation
    useEffect(() => {
        if (AppContext.isPaneOpen == true) {
            setItemDetailsPaneActive(true);
            setTimeout(() => { setItemDetailsPaneOpenEffect(true); }, 100);
        }
        else {
            setItemDetailsPaneOpenEffect(false);
            setTimeout(() => { setItemDetailsPaneActive(false); }, 300);
        }
    }, [AppContext.isPaneOpen]);

    const openContextualMenu = () => {
        setShowItemContextMenu(!showItemContextMenu);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contextMenuButton.current && contextMenuButton.current.contains(e.target) ||
                contextMenu.current && contextMenu.current.contains(e.target))
                return;

            setShowItemContextMenu(false);
            AppContext.setIsShowingDeleteConfirmation(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (visibleDetailsTab != "change-assessment" && visibleDetailsTab != "products")
            setIsEditMode(false);
    }, [visibleDetailsTab]);

    const disableAllItemFormFieldsEdit = () => {
        let newObj = { ...isEditingField };
        for (let prop in newObj) {
            newObj[prop] = false;
        }
        setIsEditingField(newObj);
    };

    const cancelEdit = () => {
        AppContext.setCurrentItem(AppContext.originalItem);
        setIsEditMode(false);
        setVersion(v => v + 1);
    }

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
        setIsShowingSaveMessage(false);
    }

    const changeTab = (tab) => {
        setIsShowingSaveMessage(false);
        setVisibleDetailsTab(tab);
    }

    const closePane = () => {
        AppContext.closePane();
    }


    const validFormField = (field, errorFunc) => {
        if (field === "" || field == null) {
            errorFunc(true);
            return false;
        }
        errorFunc(false);
        return true;
    };


    const saveCurrentItem = () => {

        let isValidForm = true;

        setErrorEditTitle(false);
        setErrorEditChangeAssessmentType(false);
        setErrorEditDueDate(false);

        setErrorEditingItem(null);

        if (!validFormField(AppContext.currentItem.Title, setErrorEditTitle))
            isValidForm = false;
        if (!validFormField(AppContext.currentItem.ChangeAssessmentType, setErrorEditChangeAssessmentType))
            isValidForm = false;
        if (!validFormField(AppContext.currentItem.DueDate, setErrorEditDueDate))
            isValidForm = false;
        
        if (!isValidForm) {
            setErrorEditingItem("Title, Description, Type and Due Date can't be empty");
            return false;
        }


        setIsSavingItem(true);
        setIsShowingSaveMessage(false);
        disableAllItemFormFieldsEdit();
        setIsShowingConfirmSavePopup(false);

        let updateItemURL = AppContext.dataUrl;

        const updatedItem = structuredClone(AppContext.currentItem);

        let requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedItem)
        };

        fetch(updateItemURL, requestOptions)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.json();
            })
            .then((result) => {
                if (result.Id) {
                    const updatedDataArray = AppContext.data.map((item) =>
                        item.Id === result.Id ? result : item
                    );
                    AppContext.setData(updatedDataArray);
                    AppContext.setCurrentItem(result);
                    AppContext.setOriginalItem(result);
                    setIsEditMode(false);
                    setIsShowingSaveMessage(true);
                }
            })
            .catch((error) => {
                console.log("error: " + error);
            })
            .finally(() => {
                setIsSavingItem(false);
            });
    }

    const updateContextData = (newItem) => {
        const updatedDataArray = AppContext.data.map(item => item.Id === newItem.Id ? newItem : item);
        AppContext.setData(updatedDataArray);
        AppContext.setCurrentItem(newItem);
        AppContext.setOriginalItem(newItem);
    };

    const openAddProductsPopup = () => {
        setProductIds([]);
        setErrorAddingProducts(null);
        setAddProductsPreview(null);
        setIsShowingConfirmAddProductsPopup(false);
        setAddProductsPopupKey(prev => prev + 1);
        setIsShowingAddProductsPopup(true);
    };

    const closeAddProductsPopup = () => {
        if (isPreviewingAddProducts || isAddingProducts) return;
        setIsShowingAddProductsPopup(false);
        setIsShowingConfirmAddProductsPopup(false);
        setProductIds([]);
        setErrorAddingProducts(null);
        setAddProductsPreview(null);
        setIsAddingProducts(false);
        setIsPreviewingAddProducts(false);
    };

    const closeConfirmAddProductsPopup = () => {
        if (isAddingProducts) return;
        setIsShowingConfirmAddProductsPopup(false);
        setAddProductsPreview(null);
        setErrorAddingProducts(null);
    };

    const previewAddProductsToAssessment = async () => {
        if (!productIds || productIds.length === 0) {
            setErrorAddingProducts("Please select at least one product.");
            return;
        }
        if (!AppContext.currentItem?.Id || isPreviewingAddProducts || isAddingProducts) return;

        setIsPreviewingAddProducts(true);
        setErrorAddingProducts(null);

        try {
            const response = await fetch(
                `/umbraco/api/RIMSRegistrations/PreviewAddChangeAssessmentProducts/?id=${AppContext.currentItem.Id}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(productIds),
                }
            );
            if (!response.ok) {
                let message = "Failed to preview products.";
                try {
                    message = await response.text();
                } catch (e) { /* ignore */ }
                throw new Error(message || "Failed to preview products.");
            }

            const preview = await response.json();
            setAddProductsPreview(preview);
            setIsShowingConfirmAddProductsPopup(true);
        } catch (error) {
            console.error("Error previewing products:", error);
            setErrorAddingProducts(error.message || "An error occurred while previewing products.");
        } finally {
            setIsPreviewingAddProducts(false);
        }
    };

    const confirmAddProductsToAssessment = () => {
        if (!productIds || productIds.length === 0) {
            setErrorAddingProducts("Please select at least one product.");
            return;
        }
        if (isAddingProducts) return;

        setIsAddingProducts(true);
        setErrorAddingProducts(null);

        const updatedItem = {
            ...structuredClone(AppContext.currentItem),
            ProductIds: productIds,
        };

        fetch(AppContext.dataUrl, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedItem),
        })
            .then(async (response) => {
                if (!response.ok) {
                    let message = "Failed to add products.";
                    try {
                        message = await response.text();
                    } catch (e) { /* ignore */ }
                    throw new Error(message || "Failed to add products.");
                }
                return response.json();
            })
            .then((result) => {
                if (result?.Id) {
                    updateContextData(result);
                    setItemListRefresh(prev => prev + 1);
                    setIsShowingConfirmAddProductsPopup(false);
                    setAddProductsPreview(null);
                    setIsShowingAddProductsPopup(false);
                    setProductIds([]);
                }
            })
            .catch((error) => {
                console.error("Error adding products:", error);
                setErrorAddingProducts(error.message || "An error occurred while adding products.");
            })
            .finally(() => {
                setIsAddingProducts(false);
            });
    };

    const handleRowUpdated = async (apiResponse) => {
        try {
            if (apiResponse && apiResponse.Progress !== undefined && apiResponse.ActionRequiredCount !== undefined) {
                updateContextData({
                    ...AppContext.currentItem,
                    Progress: apiResponse.Progress,
                    ActionRequiredCount: apiResponse.ActionRequiredCount,
                    ...(apiResponse.ActionNotRequiredCount !== undefined && { ActionNotRequiredCount: apiResponse.ActionNotRequiredCount })
                });
                return;
            }
            if (apiResponse && apiResponse.ParentAssessment) {
                updateContextData({ ...AppContext.currentItem, ...apiResponse.ParentAssessment });
                return;
            }

            const singleRes = await fetch(`/umbraco/api/RIMSRegistrations/ChangeAssessment/?id=${AppContext.currentItem.Id}`);
            if (singleRes.ok) {
                const result = await singleRes.json();
                if (result && result.Id) {
                    updateContextData({ ...AppContext.currentItem, ...result });
                }
            }
        } catch (error) {
            console.error("Error refreshing parent assessment:", error);
        }
    };

    const tabs = [
        { title: "Change Assessment", tab: "change-assessment" },
        { title: "Products", tab: "products" },
        { title: "Submissions", tab: "submissions" }
    ];

    const usersList = [{ Id: "", Name: "Choose an option" }, ...(AppContext.users || [])];
    const typeList = [
        { Id: "", Name: "Choose an option" },
        { Id: "0", Name: "Product" },
        { Id: "1", Name: "QMS" },
        { Id: "2", Name: "Legal Manufacturer" },
        { Id: "3", Name: "Manufacturing" }
    ];

    const displayFieldOnly = true;
    let fullWidthPane = true;

    return (
        <>
            {itemDetailsPaneActive && (
                <div
                    style={{ height: "100vh", overflow: "auto", backgroundColor: "#f7fafc" }}
                    className={`${fullWidthPane ? "slide-pane-full-width" : "slide-pane"} ${itemDetailsPaneOpenEffect ? "open" : ""}`}
                >
                    <div className="content">

                        <p style={{ color: "#555", fontSize: "11px" }}>
                            <span className="breadcrumb-link" style={{ marginRight: "5px" }} onClick={() => closePane()}>Change Assessments</span>
                            {(AppContext.currentItem === null || AppContext.isLoadingItemDetails) ? null :
                                <>
                                    &gt; <span style={{ marginLeft: "5px" }}> {AppContext.currentItem.Title}</span>
                                </>
                            }
                        </p>

                        <div className="item-details-content">
                            <div className="item-details-content-tabs">
                                <div className="nav" style={{ display: "flex", flexDirection: "column", gap: "4px" }} >
                                    {tabs.map((tab, index) => {
                                        if (tab.onlyAdmin && AppContext.userRole !== "Admin") {
                                            return null;
                                        }
                                        return (
                                            <a
                                                key={index}
                                                onClick={() => changeTab(tab.tab)}
                                                className={`nav-item item-details-menu-tab ${visibleDetailsTab === tab.tab ? "active" : ""}`}
                                            >
                                                {tab.title}
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="item-details-content-content">
                                {(AppContext.currentItem === null || AppContext.isLoadingItemDetails) ? (
                                    <>
                                        <LayoutComponents.PageLoading />
                                    </>
                                ) : (
                                    <>
                                        {/* Header */}
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                                <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: "#1a202c", display: "flex", alignItems: "center", gap: "10px" }}>
                                                    <i className="fas fa-clipboard-check" style={{ color: "#2b6cb0" }} />
                                                    Change Assessment
                                                </h2>
                                                <span style={{ fontSize: "16px", color: "#4a5568", fontWeight: 600, background: "#edf2f7", padding: "4px 12px", borderRadius: "6px" }}>
                                                    {AppContext.currentItem?.Title}
                                                </span>
                                            </div>

                                            <div style={{ display: "flex", gap: "8px" }}>
                                                {/* Edit controls visible in any tab if Admin */}

                                                    {visibleDetailsTab == "change-assessment" ? <>

                                                        {AppContext.userRole == "Admin" && (
                                                            <>
                                                                {isEditMode ? (
                                                                    <div id="buttom-bar" >
                                                                        <div className="action-button-bar" style={{ marginTop: "0px" }}>
                                                                            <div className="action-button-bar-left"></div>
                                                                            <div className="action-button-bar-center"></div>
                                                                            <div className="action-button-bar-right">
                                                                                {!isSavingItem ? (
                                                                                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "right", gap: "8px" }}>
                                                                                        <button
                                                                                            className="action-button save-button"
                                                                                            onClick={() => cancelEdit()}
                                                                                            onMouseOver={(e) => {
                                                                                                e.currentTarget.style.backgroundColor = "#f8f9fa";
                                                                                                e.currentTarget.style.borderColor = "#c1c4c8";
                                                                                            }}
                                                                                            onMouseOut={(e) => {
                                                                                                e.currentTarget.style.backgroundColor = "#ffffff";
                                                                                                e.currentTarget.style.borderColor = "#dee2e6";
                                                                                            }}
                                                                                            style={{
                                                                                                display: "inline-flex", alignItems: "center", padding: "6px 14px", cursor: "pointer", transition: "all 0.2s ease-in-out", color: "#5f6368", border: "1px solid #dee2e6", backgroundColor: "#ffffff", whiteSpace: "nowrap", outline: "none"
                                                                                            }}
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                        <button
                                                                                            className="action-button save-button"
                                                                                            onClick={() => { setErrorEditingItem(null); setIsShowingConfirmSavePopup(true); }}
                                                                                        >
                                                                                            Save
                                                                                        </button>
                                                                                    </div>
                                                                                ) : (
                                                                                    <LoadingSpinnerImage />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                                                        <div style={{ flex: 1, justifyContent: "right", display: "flex", alignItems: "center", marginRight: "20px" }}>
                                                                            {isShowingSaveMessage && <span className="save-message-shown">Save successful!</span>}
                                                                        </div>
                                                                        <div className="view-task-field" style={{ textAlign: "right", display: "flex", alignItems: "center", marginBottom: "0px", gap: "10px" }}>
                                                                            <span onClick={() => toggleEditMode()} value="EDIT" style={{ borderRadius: "20px" }} className="edit-rims-edit-button">
                                                                                <i className="far fa-edit pointer g-mr-8"></i>Edit Change Assessment
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                    </> : null}

                                                <button ref={contextMenuButton} onClick={openContextualMenu} style={{ border: "1px solid #cbd5e0", background: "white", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", color: "#4a5568" }}>
                                                    <i className="fas fa-ellipsis-v" />
                                                </button>
                                                <button onClick={() => closePane()} style={{ border: "1px solid #cbd5e0", background: "white", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", color: "#4a5568" }}>
                                                    <i className="fas fa-times" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Main content */}
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>

                                            {/* CHANGE ASSESSMENT TAB */}
                                            {visibleDetailsTab == "change-assessment" && (
                                                <>
                                                    {/* LEFT COLUMN */}
                                                    <div style={{ flex: 2, minWidth: "400px", display: "flex", flexDirection: "column", gap: "24px" }}>
                                                        <LayoutComponents.InfoBlock>
                                                            <h4 style={{ margin: "0 0 20px 0", fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#718096", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
                                                                General Information
                                                            </h4>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                                                <div>
                                                                    <span style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "4px" }}>Title</span>
                                                                    <span style={{ fontSize: "15px", fontWeight: 600, color: "#4a5568" }}>
                                                                        <FieldTypes.ItemDetailsField_Text AppContext={AppContext} field={"Title"} fieldTitle={"Title"} mandatory={true} forceIsEditing={isEditMode} displayFieldOnly={displayFieldOnly} />
                                                                    </span>

                                                                    {errorEditTitle && (
                                                                        <span className="form-field-error-message">
                                                                            This field is necessary
                                                                        </span>
                                                                    )}

                                                                </div>

                                                                <div>
                                                                    <span style={{ display: "block", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#a0aec0", marginBottom: "4px" }}>Description</span>
                                                                    <span style={{ fontSize: "15px", color: "#4a5568" }}>
                                                                        <FieldTypes.ItemDetailsField_Text AppContext={AppContext} field={"Description"} fieldTitle={"Description"} mandatory={false} isTextarea={true} forceIsEditing={isEditMode} displayFieldOnly={displayFieldOnly} />
                                                                    </span>
                                                                </div>

                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#a0aec0", textTransform: "uppercase", marginBottom: "4px" }}>
                                                                        Type
                                                                    </label>
                                                                    {isEditMode ? (
                                                                        <FieldTypes.ItemDetailsField_Dropdown
                                                                            AppContext={AppContext}
                                                                            fieldId={"ChangeAssessmentType"}
                                                                            fieldName={"_ChangeAssessmentType"}
                                                                            fieldTitle={"Type"}
                                                                            LOV={typeList}
                                                                            LOVIdField={"Id"}
                                                                            LOVNameField={"Name"}
                                                                            mandatory={true}
                                                                            enableSearch={false}
                                                                            forceIsEditing={isEditMode}
                                                                            displayFieldOnly={displayFieldOnly}
                                                                        />
                                                                    ) : (
                                                                        <span style={{ fontWeight: 600, fontSize: "15px", color: "#4a5568" }}>
                                                                            {AppContext.currentItem?.ChangeAssessmentType !== null && AppContext.currentItem?.ChangeAssessmentType !== undefined && AppContext.currentItem?.ChangeAssessmentType !== ""
                                                                                ? (
                                                                                    typeList.find(t => String(t.Id).trim() === String(AppContext.currentItem.ChangeAssessmentType).trim())?.Name
                                                                                    || AppContext.currentItem.ChangeAssessmentType
                                                                                )
                                                                                : "---"}
                                                                        </span>
                                                                        )}

                                                                    {errorEditChangeAssessmentType && (
                                                                        <span className="form-field-error-message">
                                                                            This field is necessary
                                                                        </span>
                                                                    )}

                                                                </div>

                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#a0aec0", textTransform: "uppercase", marginBottom: "4px" }}>Due Date</label>
                                                                    {isEditMode ? <>
                                                                        <FieldTypes.ItemDetailsField_DateUTC AppContext={AppContext} field={"DueDate"} fieldTitle={"Due Date"} mandatory={false} forceIsEditing={isEditMode} displayFieldOnly={displayFieldOnly} />
                                                                    </> : <>
                                                                        <span style={{ fontSize: "15px", color: "#4a5568" }}>
                                                                            {AppContext.currentItem?.DueDate ? <FieldTypes.FieldType_DateWithExpiredWarningUTC value={AppContext.currentItem.DueDate} /> : "---"}
                                                                        </span>
                                                                        </>}

                                                                    {errorEditDueDate && (
                                                                        <span className="form-field-error-message">
                                                                            This field is necessary
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#a0aec0", textTransform: "uppercase", marginBottom: "4px" }}>Owner</label>
                                                                    {isEditMode ? <>
                                                                        <FieldTypes.ItemDetailsField_Dropdown AppContext={AppContext} fieldId={"ResponsibleOwner"} fieldName={"_ResponsibleOwner"} fieldTitle={"Owner"} LOV={usersList} LOVIdField={"Id"} LOVNameField={"Name"} mandatory={true} enableSearch={true} forceIsEditing={isEditMode} displayFieldOnly={displayFieldOnly} />
                                                                    </> : <>
                                                                        <div style={{ display: "flex", alignItems: "center", fontSize: "15px", color: "#4a5568" }}>
                                                                            {AppContext.currentItem?.ResponsibleOwner ?
                                                                                <FieldTypes.FieldType_FullUser users={AppContext.users} value={AppContext.currentItem.ResponsibleOwner} />
                                                                                : "---"}
                                                                        </div>
                                                                    </>}
                                                                </div>
                                                            </div>
                                                        </LayoutComponents.InfoBlock>
                                                    </div>

                                                    {/* RIGHT COLUMN */}
                                                    <div style={{ flex: 1, minWidth: "280px", display: "flex", flexDirection: "column", gap: "24px" }}>
                                                        <LayoutComponents.InfoBlock>
                                                            <h4 style={{ margin: "0 0 16px 0", fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#718096" }}>
                                                                Assessment Details
                                                            </h4>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                                                                {/* Total Products */}
                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#a0aec0", textTransform: "uppercase", marginBottom: "4px" }}>
                                                                        Total Products
                                                                    </label>
                                                                    <span style={{ fontWeight: 600, fontSize: "15px", color: "#4a5568" }}>
                                                                        {AppContext.currentItem?.ProductCount !== null && AppContext.currentItem?.ProductCount !== undefined && AppContext.currentItem?.ProductCount !== ""
                                                                            ? AppContext.currentItem.ProductCount
                                                                            : "---"}
                                                                    </span>
                                                                </div>

                                                                {/* Total Certificates */}
                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#a0aec0", textTransform: "uppercase", marginBottom: "4px" }}>
                                                                        Total Certificates
                                                                    </label>
                                                                    <span style={{ fontWeight: 600, fontSize: "15px", color: "#4a5568" }}>
                                                                        {AppContext.currentItem?.CertificateCount !== null && AppContext.currentItem?.CertificateCount !== undefined && AppContext.currentItem?.CertificateCount !== ""
                                                                            ? AppContext.currentItem.CertificateCount
                                                                            : "---"}
                                                                    </span>
                                                                </div>

                                                                {/* Total Countries */}
                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#a0aec0", textTransform: "uppercase", marginBottom: "4px" }}>
                                                                        Total Countries
                                                                    </label>
                                                                    <span style={{ fontWeight: 600, fontSize: "15px", color: "#4a5568" }}>
                                                                        {AppContext.currentItem?.CountryCount !== null && AppContext.currentItem?.CountryCount !== undefined && AppContext.currentItem?.CountryCount !== ""
                                                                            ? AppContext.currentItem.CountryCount
                                                                            : "---"}
                                                                    </span>
                                                                </div>

                                                                {/* Progress Bar */}
                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#a0aec0", textTransform: "uppercase", marginBottom: "8px" }}>
                                                                        Progress
                                                                    </label>
                                                                    {AppContext.currentItem?.Progress !== null && AppContext.currentItem?.Progress !== undefined && AppContext.currentItem?.Progress !== "" ? (
                                                                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                                            <div style={{ flex: 1, height: "8px", backgroundColor: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                                                                                <div style={{
                                                                                    width: `${Math.min(Math.max(Number(AppContext.currentItem.Progress), 0), 100)}%`,
                                                                                    backgroundColor: Number(AppContext.currentItem.Progress) === 100 ? "#48bb78" : "#3182ce",
                                                                                    height: "100%",
                                                                                    transition: "width 0.4s ease"
                                                                                }} />
                                                                            </div>
                                                                            <span style={{ fontSize: "13px", fontWeight: 600, color: "#4a5568", minWidth: "40px", textAlign: "right" }}>
                                                                                {Number(AppContext.currentItem.Progress).toFixed(2).replace(/\.00$/, '')}%
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ fontWeight: 500, fontSize: "15px", color: "#4a5568" }}>---</span>
                                                                    )}
                                                                </div>

                                                                {/* Action Required Pie Chart */}
                                                                <div>
                                                                    <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#a0aec0", textTransform: "uppercase", marginBottom: "8px" }}>
                                                                        Actions Required
                                                                    </label>
                                                                    {AppContext.currentItem?.ActionRequiredCount !== null && AppContext.currentItem?.ActionRequiredCount !== undefined ? (
                                                                        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                                                            {(() => {
                                                                                const actionRequired = Number(AppContext.currentItem.ActionRequiredCount) || 0;
                                                                                const actionNotRequired = Number(AppContext.currentItem.ActionNotRequiredCount) || 0;
                                                                                const totalActions = actionRequired + actionNotRequired;

                                                                                const totalItems = Number(AppContext.currentItem.CertificateCount) ||
                                                                                    Number(AppContext.currentItem.TotalCount) ||
                                                                                    (actionRequired + actionNotRequired);
                                                                                const noAnswerCount = Math.max(0, totalItems - (actionRequired + actionNotRequired));

                                                                                const requiredPct = AppContext.currentItem.ActionRequiredPercentage !== undefined && AppContext.currentItem.ActionRequiredPercentage !== null
                                                                                    ? Number(AppContext.currentItem.ActionRequiredPercentage)
                                                                                    : (totalItems > 0 ? (actionRequired / totalItems) * 100 : 0);

                                                                                const notRequiredPct = AppContext.currentItem.ActionNotRequiredPercentage !== undefined && AppContext.currentItem.ActionNotRequiredPercentage !== null
                                                                                    ? Number(AppContext.currentItem.ActionNotRequiredPercentage)
                                                                                    : (totalItems > 0 ? (actionNotRequired / totalItems) * 100 : 0);

                                                                                const noAnswerPct = Math.max(0, +(100 - (requiredPct + notRequiredPct)).toFixed(2));

                                                                                const stop1 = requiredPct;
                                                                                const stop2 = requiredPct + notRequiredPct;
                                                                                let totalCount = actionRequired + actionNotRequired + noAnswerCount;

                                                                                return (
                                                                                    <>
                                                                                        <div style={{
                                                                                            width: "97px",
                                                                                            height: "97px",
                                                                                            borderRadius: "50%",
                                                                                            flexShrink: 0,
                                                                                            background: totalActions === 0
                                                                                                ? "#e2e8f0"
                                                                                                : `conic-gradient(#e53e3e 0% ${stop1}%, #38a169 ${stop1}% ${stop2}%, #cbd5e0 ${stop2}% 100%)`
                                                                                        }} />

                                                                                        <div style={{ display: "flex", flexDirection: "column", fontSize: "13px", color: "#4a5568", gap: "4px" }}>
                                                                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                                                <span style={{ width: "10px", height: "10px", backgroundColor: "#e53e3e", borderRadius: "50%" }}></span>
                                                                                                <span>Required: <strong>{actionRequired} ({requiredPct} %)</strong></span>
                                                                                            </div>
                                                                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                                                <span style={{ width: "10px", height: "10px", backgroundColor: "#38a169", borderRadius: "50%" }}></span>
                                                                                                <span>Not Required: <strong>{actionNotRequired} ({notRequiredPct} %)</strong></span>
                                                                                            </div>
                                                                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                                                <span style={{ width: "10px", height: "10px", backgroundColor: "#cbd5e0", borderRadius: "50%" }}></span>
                                                                                                <span>No Answer: <strong>{noAnswerCount} ({noAnswerPct}%)</strong></span>
                                                                                            </div>
                                                                                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px", paddingTop: "4px", borderTop: "1px solid #edf2f7" }}>
                                                                                                <span>Total: <strong>{totalCount}</strong></span>
                                                                                            </div>
                                                                                        </div>
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    ) : (
                                                                        <span style={{ fontWeight: 500, fontSize: "15px", color: "#4a5568" }}>---</span>
                                                                    )}
                                                                </div>

                                                            </div>
                                                        </LayoutComponents.InfoBlock>
                                                    </div>


                                                    {/* Custom Fields*/}
                                                    <div style={{ width: "100%" }}>
                                                        <LayoutComponents.InfoBlock>
                                                        <h4 style={{ margin: "0 0 16px 0", fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#718096" }}>
                                                            Custom Fields
                                                        </h4>
                                                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                                            {/* CUSTOM USER FIELDS */}
                                                            {/* CUSTOM USER FIELDS */}
                                                            {/* CUSTOM USER FIELDS */}
                                                            {AppContext.enableCustomFields ?
                                                                <FieldTypes.FieldType_CustomUserFieldsView key={`custom_fields_${version}`} globalContext={AppContext} isEditing={isEditMode} />
                                                                : null}
                                                        </div>
                                                    </LayoutComponents.InfoBlock>
                                                    </div>
                                                    {/* Items Table included in Change Assessment tab */}
                                                    <div style={{ width: "100%" }}>
                                                        <LayoutComponents.InfoBlock>
                                                            <h4 style={{ margin: "0 0 20px 0", fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#718096", borderBottom: "1px solid #edf2f7", paddingBottom: "10px" }}>
                                                             Impacted Certificates & Countries
                                                            </h4>
                                                            <div style={{ marginBottom: "20px" }}>
                                                                <ItemsList
                                                                    AppContext={AppContext}
                                                                    refresh={itemListRefresh}
                                                                    setRefresh={setItemListRefresh}
                                                                    onRowUpdated={handleRowUpdated}
                                                                    onAddProducts={openAddProductsPopup}
                                                                />
                                                            </div>
                                                        </LayoutComponents.InfoBlock>
                                                    </div>
                                                </>
                                            )}

                                            {/* PRODUCTS TAB */}
                                            {visibleDetailsTab == "products" && (
                                                <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
                                                    <LayoutComponents.InfoBlock>
                                                            <h4 style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                                margin: "0 0 20px 0",
                                                                fontSize: "14px",
                                                                fontWeight: 700,
                                                                textTransform: "uppercase",
                                                                letterSpacing: "0.5px",
                                                                color: "#718096",
                                                                borderBottom: "1px solid #edf2f7",
                                                                paddingBottom: "10px"
                                                            }}>
                                                                <span>Products Affected</span>
                                                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                                    <span style={{ fontSize: "12px", color: "#a0aec0", textTransform: "none", letterSpacing: "normal", fontWeight: 500 }}>
                                                                        Count: {AppContext.currentItem?.Products?.length || 0}
                                                                    </span>
                                                                    {AppContext.userRole === "Admin" && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={openAddProductsPopup}
                                                                            style={{
                                                                                padding: "6px 12px",
                                                                                background: "#3398dc",
                                                                                color: "#f7f7f7",
                                                                                border: "none",
                                                                                borderRadius: "15px",
                                                                                cursor: "pointer",
                                                                                fontSize: "12px",
                                                                                fontWeight: 500,
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: "6px",
                                                                                textTransform: "none",
                                                                                letterSpacing: "normal",
                                                                            }}
                                                                        >
                                                                            <i className="fas fa-plus-circle"></i>
                                                                            Add Products
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </h4>
                                                        <div style={{ paddingLeft: "5px" }}>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                                                {AppContext.currentItem?.Products?.length > 0 ? (
                                                                    AppContext.currentItem.Products.map((product, idx) => (
                                                                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "#2d3748" }}>
                                                                            <span style={{ fontWeight: "600", color: "#2b6cb0", background: "#ebf8ff", padding: "2px 8px", borderRadius: "4px" }}>
                                                                                {product.ProductCode || product.Code || product.SKU}
                                                                            </span>
                                                                            <a
                                                                                href={`/your-medboard/products/?productId=${product.Id || product.ProductId}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{ color: "#3182ce", textDecoration: "none", fontWeight: "500" }}
                                                                                onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
                                                                                onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
                                                                            >
                                                                                {product.ProductSku}  •  {product.ProductName}
                                                                            </a>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <span style={{ color: "#a0aec0", fontSize: "14px", fontStyle: "italic" }}>
                                                                        No products selected
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </LayoutComponents.InfoBlock>
                                                </div>
                                            )}
                                            {/* SUBMISSIONS TAB */}
                                            {visibleDetailsTab == "submissions" && (
                                               <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
                                                        <div style={{ marginBottom: "20px" }}>
                                                        <SubmissionsTable
                                                                AppContext={AppContext}
                                                        />
                                                        </div>
                                                </div>
                                            )}

                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CONTEXT MENU */}
            {showItemContextMenu && (
                <div
                    ref={contextMenu}
                    style={{
                        position: "fixed",
                        top: `50px`,
                        right: `70px`,
                        background: "white",
                        border: "1px solid #ccc",
                        padding: "4px",
                        borderRadius: "15px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                    }}
                >
                    {AppContext.userRole === "Admin" ? (
                        !AppContext.isShowingDeleteConfirmation ? (
                            <div className="context-menu-option" onClick={() => AppContext.setIsShowingDeleteConfirmation(true)}>
                                <i className="far fa-trash-alt"></i> Delete this item
                            </div>
                        ) : (
                            AppContext.isDeletingItem ? (
                                <LoadingSpinnerImage />
                            ) : (
                                <div className="context-menu-option context-delete-option" onClick={AppContext.deleteCurrentItem}>
                                    <i className="far fa-trash-alt"></i> Confirm delete?
                                </div>
                            )
                        )
                    ) : (
                        <div className="context-menu-option disabled">
                            <i className="far fa-trash-alt"></i> Delete (No Permission)
                        </div>
                    )}
                </div>
            )}

            {/* ADD PRODUCTS POPUP */}
            {isShowingAddProductsPopup && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "flex",
                        justifyContent: "center",
                        zIndex: 1000,
                        overflowY: "auto",
                        alignItems: "flex-start",
                    }}
                >
                    <div
                        style={{
                            width: "50%",
                            zIndex: 1000,
                            border: "1px solid rgb(51, 51, 51)",
                            padding: "15px",
                            backgroundColor: "rgb(250, 250, 250)",
                            borderRadius: "10px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                            margin: "120px auto 40px auto",
                        }}
                    >
                        <div style={{ display: "flex", marginBottom: "5px" }}>
                            <div style={{ width: "50%" }}>
                                <h3>Add Products</h3>
                            </div>
                            <div style={{ width: "50%", textAlign: "right" }}>
                                <button
                                    onClick={closeAddProductsPopup}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        fontSize: "18px",
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
                            <ProductTreeSelector
                                key={addProductsPopupKey}
                                onSelectionChange={setProductIds}
                            />
                        </div>

                        <div className="action-button-bar" style={{ marginTop: "10px" }}>
                            <div className="action-button-bar-left">
                                <button
                                    className="action-button cancel-button"
                                    onClick={closeAddProductsPopup}
                                    disabled={isPreviewingAddProducts || isAddingProducts}
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="action-button-bar-center">
                                {!isShowingConfirmAddProductsPopup && errorAddingProducts ? (
                                    <div style={{ color: "#ff5400" }}>{errorAddingProducts}</div>
                                ) : null}
                            </div>
                            <div className="action-button-bar-right">
                                {isPreviewingAddProducts ? (
                                    <LoadingSpinnerImage />
                                ) : (
                                    <button
                                        className="action-button save-button"
                                        onClick={previewAddProductsToAssessment}
                                        disabled={isAddingProducts}
                                    >
                                        Add Products
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* CONFIRM ADD PRODUCTS POPUP */}
            {isShowingConfirmAddProductsPopup && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "flex",
                        justifyContent: "center",
                        zIndex: 1001,
                        overflowY: "auto",
                        alignItems: "flex-start",
                    }}
                >
                    <div
                        style={{
                            width: "50%",
                            zIndex: 1001,
                            border: "1px solid rgb(51, 51, 51)",
                            padding: "15px",
                            backgroundColor: "rgb(250, 250, 250)",
                            borderRadius: "10px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                            margin: "120px auto 40px auto",
                        }}
                    >
                        <div style={{ display: "flex", marginBottom: "5px" }}>
                            <div style={{ width: "50%" }}>
                                <h3>Confirm Add Products</h3>
                            </div>
                            <div style={{ width: "50%", textAlign: "right" }}>
                                <button
                                    onClick={closeConfirmAddProductsPopup}
                                    disabled={isAddingProducts}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: isAddingProducts ? "default" : "pointer",
                                        fontSize: "18px",
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
                            {(addProductsPreview?.NewProductCount ?? 0) > 0 ? (
                                <p>
                                    Adding <strong>{addProductsPreview.NewProductCount} product{addProductsPreview.NewProductCount === 1 ? "" : "s"}</strong>
                                    {(addProductsPreview.TotalCertificatesFromProducts ?? 0) > 0
                                        ? ` (${addProductsPreview.TotalCertificatesFromProducts} certificate${addProductsPreview.TotalCertificatesFromProducts === 1 ? "" : "s"})`
                                        : ""}
                                    {" "}to this change assessment. This will add <strong>{addProductsPreview.CertificatesToAdd ?? 0} new certificate{(addProductsPreview.CertificatesToAdd ?? 0) === 1 ? "" : "s"}</strong>
                                    {(addProductsPreview.CertificatesAlreadyLinked ?? 0) > 0
                                        ? ` (${addProductsPreview.CertificatesAlreadyLinked} already exist${addProductsPreview.CertificatesAlreadyLinked === 1 ? "s" : ""})`
                                        : ""}
                                    .
                                </p>
                            ) : (
                                <p>
                                    The selected products are already on this change assessment.
                                </p>
                            )}

                            <div className="action-button-bar" style={{ marginTop: "10px" }}>
                                <div className="action-button-bar-left">
                                    <button
                                        className="action-button cancel-button"
                                        onClick={closeConfirmAddProductsPopup}
                                        disabled={isAddingProducts}
                                    >
                                        {(addProductsPreview?.NewProductCount ?? 0) > 0 ? "Cancel" : "Close"}
                                    </button>
                                </div>
                                <div className="action-button-bar-center">
                                    {errorAddingProducts ? (
                                        <div style={{ color: "#ff5400" }}>{errorAddingProducts}</div>
                                    ) : null}
                                </div>
                                <div className="action-button-bar-right">
                                    {(addProductsPreview?.NewProductCount ?? 0) > 0 ? (
                                        isAddingProducts ? (
                                            <LoadingSpinnerImage />
                                        ) : (
                                            <button
                                                className="action-button save-button"
                                                onClick={confirmAddProductsToAssessment}
                                            >
                                                Confirm
                                            </button>
                                        )
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SAVE MODAL */}
            {isShowingConfirmSavePopup && (
                <div
                    style={{
                        position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.4)",
                        display: "flex", justifyContent: "center", zIndex: 1000, overflowY: "auto", alignItems: "flex-start"
                    }}
                >
                    <div style={{
                        width: "50%", zIndex: 1000, border: "1px solid rgb(51, 51, 51)", padding: "15px",
                        backgroundColor: "rgb(250, 250, 250)", borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                        margin: "120px auto 40px auto",
                    }}>
                        <div style={{ display: "flex", marginBottom: "5px" }}>
                            <div style={{ width: "50%" }}>
                                <h3>Save changes</h3>
                            </div>
                            <div style={{ width: "50%", textAlign: "right" }}>
                                <button
                                    onClick={() => setIsShowingConfirmSavePopup(false)}
                                    style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "18px" }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
                            Are you sure you want to save the assessment properties?
                            <br /><br />
                            {AppContext.userRole == "Admin" && (
                                <div id="buttom-bar" >
                                    <div className="action-button-bar" style={{ marginTop: "10px" }}>
                                        <div className="action-button-bar-left">
                                            <button className="action-button cancel-button" onClick={() => setIsShowingConfirmSavePopup(false)}>
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="action-button-bar-center">
                                            {errorEditingItem ? <div style={{ color: "#ff5400" }}>Error: {errorEditingItem}</div> : null}
                                        </div>
                                        <div className="action-button-bar-right">
                                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "right", gap: "8px" }}>
                                                <button
                                                    className="action-button save-button"
                                                    onClick={() => saveCurrentItem()}
                                                    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#1a73e8"; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "#1a73e8"; }}
                                                    style={{
                                                        display: "inline-flex", alignItems: "center", padding: "6px 14px", cursor: "pointer",
                                                        transition: "all 0.2s ease-in-out", color: "#ffffff", border: "1px solid #1a73e8",
                                                        backgroundColor: "#1a73e8", whiteSpace: "nowrap", outline: "none"
                                                    }}
                                                >
                                                    Save Assessment
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Generic Items Table Replacing the Registrations Table
const ItemsList = ({ AppContext, refresh, setRefresh, onRowUpdated, onAddProducts }) => {

    // Local state for inline row editing
    const [editingRowId, setEditingRowId] = useState(null);
    const [editData, setEditData] = useState({});
    const [isSavingRow, setIsSavingRow] = useState(false);
    const [internalRefresh, setInternalRefresh] = useState(0);
    const [isFindingCertificates, setIsFindingCertificates] = useState(false);
    const [isShowingFindCertificatesPopup, setIsShowingFindCertificatesPopup] = useState(false);
    const [isConfirmingFindCertificates, setIsConfirmingFindCertificates] = useState(false);
    const [findCertificatesPreview, setFindCertificatesPreview] = useState(null);
    const [findCertificatesError, setFindCertificatesError] = useState(null);

    // Track which row is currently generating a submission
    const [creatingSubmissionId, setCreatingSubmissionId] = useState(null);

    const [isShowingSubmissionsCreatePopup, setIsShowingSubmissionsCreatePopup] = useState(false);
    const [submissionsNewItemCustomFields, setSubmittionsNewItemCustomFields] = useState([]);
    const [submissionsNewItemCustomFieldsAnswers, setSubmissionsNewItemCustomFieldsAnswers] = useState([]);
    const [isShowingSubmissionsCreateSuccessPopup, setIsShowingSubmissionsCreateSuccessPopup] = useState(false);

    const [submissionDefaults, setSubmissionDefaults] = useState({
        title: "",
        assignedTo: null,
        certificateId: null,
        certificateName: ""
    });
    
        // Create a custom AppContext for submissions (to not use the Change Assessment one)

    const submissionsContext = useMemo(() => ({

        newItemCustomFieldsAnswers: [],
        newItemCustomFields: submissionsNewItemCustomFields,
        setNewItemCustomFields: setSubmittionsNewItemCustomFields,
        newItemCustomFieldsAnswers: submissionsNewItemCustomFieldsAnswers,
        setNewItemCustomFieldsAnswers: setSubmissionsNewItemCustomFieldsAnswers,
        dataUrl: "/umbraco/api/Submission/Submissions",
        data: [],
        setData: () => { /* This is called on success, use this to show success popup and refresh the list */ setIsShowingSubmissionsCreateSuccessPopup(true); setRefresh(prev => prev + 1); },
        showAddItemPopup: () => { },
        refreshGraphs: () => { },
        singleItemTitle: "Submission",
        showAddItemPopup: (val) => { setIsShowingSubmissionsCreatePopup(val); if (val == false) setCreatingSubmissionId(null); },
        users: AppContext.users,
        SubmissionStatus: AppContext.SubmissionStatus,
        submissionPossibleStatuses: AppContext.submissionPossibleStatuses,
        SubmissionPriority: AppContext.SubmissionPriority,
        submissionPriorities: AppContext.submissionPriorities,
        SubmissionType: AppContext.SubmissionType,
        submissionTypes: AppContext.submissionTypes,
        enableCustomFields: true,
        entityTypeId: 10,
        defaultSubmissionTitle: submissionDefaults.title,
        defaultSubmissionAssignedTo: submissionDefaults.assignedTo,
        defaultSubmissionCertificateId: submissionDefaults.certificateId,
        defaultSubmissionCertificateName: submissionDefaults.certificateName,
        ChangeAssessmentItemId: submissionDefaults.changeAssessmentItemId,

    }), [submissionDefaults, submissionsNewItemCustomFields, submissionsNewItemCustomFieldsAnswers]);

    
    
    
    

    


    const closeFindCertificatesPopup = () => {
        if (isConfirmingFindCertificates) return;
        setIsShowingFindCertificatesPopup(false);
        setFindCertificatesPreview(null);
        setFindCertificatesError(null);
    };

    const handleFindNewCertificates = async () => {
        if (isFindingCertificates || isConfirmingFindCertificates || !AppContext.currentItem?.Id) return;

        setIsFindingCertificates(true);
        setFindCertificatesError(null);
        setFindCertificatesPreview(null);

        try {
            const response = await fetch(
                `/umbraco/api/RIMSRegistrations/FindChangeAssessmentCertificates/?id=${AppContext.currentItem.Id}`
            );
            if (!response.ok) throw new Error("Failed to find new certificates.");

            const preview = await response.json();
            setFindCertificatesPreview(preview);
            setIsShowingFindCertificatesPopup(true);
        } catch (error) {
            console.error("Error finding new certificates: ", error);
            alert("An error occurred while finding new certificates.");
        } finally {
            setIsFindingCertificates(false);
        }
    };

    const confirmFindNewCertificates = async () => {
        if (isConfirmingFindCertificates || !AppContext.currentItem?.Id) return;
        if (!findCertificatesPreview || findCertificatesPreview.CertificatesToAdd <= 0) {
            closeFindCertificatesPopup();
            return;
        }

        setIsConfirmingFindCertificates(true);
        setFindCertificatesError(null);

        try {
            const response = await fetch(
                `/umbraco/api/RIMSRegistrations/ChangeAssessmentItems/?id=${AppContext.currentItem.Id}`,
                { method: "POST" }
            );
            if (!response.ok) throw new Error("Failed to add new certificates.");

            setIsShowingFindCertificatesPopup(false);
            setFindCertificatesPreview(null);
            setInternalRefresh(prev => prev + 1);
            if (onRowUpdated) {
                onRowUpdated({});
            }
        } catch (error) {
            console.error("Error adding new certificates: ", error);
            setFindCertificatesError(error.message || "An error occurred while adding new certificates.");
        } finally {
            setIsConfirmingFindCertificates(false);
        }
    };

    // NEW: Async function to handle the POST request
    const handleCreateSubmission = async (row) => {

        setCreatingSubmissionId(row.Id);

        setSubmissionDefaults({
            title: "Change Assessment: " + AppContext.currentItem.Title + " (" + row.CertificateName + ")",
            assignedTo: AppContext.currentItem.ResponsibleOwner ? AppContext.currentItem.ResponsibleOwner : null,
            certificateId: row.CertificateId,
            certificateName: row.CertificateName,
            changeAssessmentItemId: row.Id,
        });

        setIsShowingSubmissionsCreatePopup(true);

    };






    const startEdit = (row) => {
        setEditingRowId(row.Id);

        let actionReq = null;
        if (row.ActionRequired !== null && row.ActionRequired !== undefined && row.ActionRequired !== "") {
            actionReq = row.ActionRequired === true || String(row.ActionRequired).toLowerCase() === 'yes' || String(row.ActionRequired).toLowerCase() === 'true';
        }

        setEditData({
            ActionRequired: actionReq,
            DescriptionAndTimelines: row.DescriptionAndTimelines || ""
        });
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setEditData({});
    };

    const saveRow = (row) => {
        setIsSavingRow(true);

        const updatedRow = {
            ...row,
            ActionRequired: editData.ActionRequired,
            DescriptionAndTimelines: editData.DescriptionAndTimelines
        };

        const updateUrl = "/umbraco/api/RIMSRegistrations/ChangeAssessmentItems/";

        fetch(updateUrl, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedRow)
        })
            .then(async response => {
                if (!response.ok) throw new Error("Failed to save item changes.");
                try {
                    return await response.json();
                } catch (e) {
                    return {};
                }
            })
            .then(data => {
                setEditingRowId(null);
                setEditData({});
                setInternalRefresh(prev => prev + 1);

                if (onRowUpdated) {
                    onRowUpdated(data);
                }
            })
            .catch(error => {
                console.error("Error updating row: ", error);
                alert("An error occurred while saving the row.");
            })
            .finally(() => {
                setIsSavingRow(false);
            });
    };

    const certificatesToAdd = findCertificatesPreview?.CertificatesToAdd ?? 0;
    const certificatesAlreadyLinked = findCertificatesPreview?.CertificatesAlreadyLinked ?? 0;
    const linkedProductCount = findCertificatesPreview?.LinkedProductCount ?? 0;

    return <>

        {isShowingFindCertificatesPopup && (
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 1000,
                    overflowY: "auto",
                    alignItems: "flex-start",
                }}
            >
                <div
                    style={{
                        width: "50%",
                        zIndex: 1000,
                        border: "1px solid rgb(51, 51, 51)",
                        padding: "15px",
                        backgroundColor: "rgb(250, 250, 250)",
                        borderRadius: "10px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                        margin: "120px auto 40px auto",
                    }}
                >
                    <div style={{ display: "flex", marginBottom: "5px" }}>
                        <div style={{ width: "50%" }}>
                            <h3>Find New Certificates</h3>
                        </div>
                        <div style={{ width: "50%", textAlign: "right" }}>
                            <button
                                onClick={closeFindCertificatesPopup}
                                disabled={isConfirmingFindCertificates}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: isConfirmingFindCertificates ? "default" : "pointer",
                                    fontSize: "18px",
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    </div>

                    <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
                        {linkedProductCount === 0 ? (
                            <p>There are no products linked to this change assessment. Link products first, then find certificates.</p>
                        ) : certificatesToAdd > 0 ? (
                            <>
                                <p>
                                    This will add <strong>{certificatesToAdd}</strong> certificate{certificatesToAdd === 1 ? "" : "s"} from the linked products.
                                </p>
                                <p style={{ marginTop: "8px" }}>
                                    {certificatesAlreadyLinked > 0
                                        ? `${certificatesAlreadyLinked} certificate${certificatesAlreadyLinked === 1 ? " is" : "s are"} already linked and will be skipped.`
                                        : "None of these certificates are already linked to this change assessment."}
                                </p>
                                <p style={{ marginTop: "8px" }}>
                                    Based on {linkedProductCount} linked product{linkedProductCount === 1 ? "" : "s"}. Do you want to continue?
                                </p>
                            </>
                        ) : (
                            <p>
                                No new certificates to add from the {linkedProductCount} linked product{linkedProductCount === 1 ? "" : "s"}
                                {certificatesAlreadyLinked > 0
                                    ? ` (${certificatesAlreadyLinked} certificate${certificatesAlreadyLinked === 1 ? " is" : "s are"} already linked).`
                                    : "."}
                            </p>
                        )}

                        <div className="action-button-bar" style={{ marginTop: "10px" }}>
                            <div className="action-button-bar-left">
                                <button
                                    className="action-button cancel-button"
                                    onClick={closeFindCertificatesPopup}
                                    disabled={isConfirmingFindCertificates}
                                >
                                    {certificatesToAdd > 0 ? "Cancel" : "Close"}
                                </button>
                            </div>
                            <div className="action-button-bar-center">
                                {findCertificatesError ? (
                                    <div style={{ color: "#ff5400" }}>{findCertificatesError}</div>
                                ) : null}
                            </div>
                            <div className="action-button-bar-right">
                                {certificatesToAdd > 0 ? (
                                    isConfirmingFindCertificates ? (
                                        <LoadingSpinnerImage />
                                    ) : (
                                        <button
                                            className="action-button save-button"
                                            onClick={confirmFindNewCertificates}
                                        >
                                            Add Certificates
                                        </button>
                                    )
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {isShowingSubmissionsCreatePopup ? <>

            <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.65)", zIndex: 11, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "10vh", overflowY: "auto", }}>
                <SubmissionsAddItemPopup globalContext={submissionsContext} />
            </div>

        </> : null}


        {isShowingSubmissionsCreateSuccessPopup ? <>


            <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.65)", zIndex: 11, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "10vh", overflowY: "auto", }}>
                <div style={{ width: "500px", maxWidth: "calc(100% - 40px)", borderRadius: "12px", border: "1px solid #d0d0d0", backgroundColor: "#fafafa", boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25)", overflow: "hidden", }}>
                        
					{/* Header */}
                        
					<div style={{padding: "18px 24px", borderBottom: "1px solid #e0e0e0",}}>

                        <div style={{ display: "flex", flexDirection: "row" }} >
                            <div style={{ flex: "1 1 0%" }} ><h3>Success</h3></div>
                            <div style={{ textAlign: "right" }}><button className="close-button" onClick={() => setIsShowingSubmissionsCreateSuccessPopup(false)}><i className="fas fa-times"></i></button></div>
                        </div>

                    </div>

                    {/* Content */}

                    <div style={{padding: "25px 24px", backgroundColor: "#f3fff3"}}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px",}}>
                            Submission created succesfully!
                        </div>
                    </div>

                    {/* Footer */}

                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", padding: "15px 24px", borderTop: "1px solid #e0e0e0", backgroundColor: "#f5f5f5", }} >
                        <div onClick={() => setIsShowingSubmissionsCreateSuccessPopup(false)} className="action-button cancel-button">
                            OK
                        </div>
                    </div>
                </div>
            </div>


        </> : null}



        <GenericDataTable
            columns={[
                {
                    key: "IdCheckbox", label: "", sortable: false, type: "checkbox", size: "60px",
                },
                {
                    key: "CertificateName", label: "Certificate Name", sortable: false, size: "260px",
                    render: ({ row, value }) => (
                        <div style={{ fontWeight: "500", color: "#2d3748" }}>
                            {value ? (
                                <a
                                    href={`/your-medboard/country-registrations/certificates/?certificateId=${row.CertificateId || row.Id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#3182ce", textDecoration: "none" }}
                                    onMouseOver={(e) => e.currentTarget.style.textDecoration = "underline"}
                                    onMouseOut={(e) => e.currentTarget.style.textDecoration = "none"}
                                >
                                    {value} ↗
                                </a>
                            ) : (
                                "---"
                            )}
                        </div>
                    ),
                },
                {
                    key: "CountryName", label: "Country", sortable: false, size: "180px",
                    render: ({ row, value }) => (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {row.FlagLink && (
                                <img
                                    className="countryFlag"
                                    src={row.FlagLink}
                                    style={{ height: "20px", marginRight: "5px" }}
                                    alt=""
                                />
                            )}
                            {value || row.CountryName || "---"}
                        </div>
                    ),
                },
                {
                    key: "ActionRequired", label: "Action Required", sortable: false, size: "130px",
                    render: ({ row, value }) => {
                        if (editingRowId === row.Id) {
                            return (
                                <select
                                    value={editData.ActionRequired === null ? "" : editData.ActionRequired}
                                    onChange={(e) => setEditData({ ...editData, ActionRequired: e.target.value === "true" })}
                                    style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #cbd5e0", fontSize: "14px", width: "100%" }}
                                >
                                    {editData.ActionRequired === null && (
                                        <option value="" disabled hidden>Choose...</option>
                                    )}
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            );
                        }

                        if (value === null || value === undefined || value === "") {
                            return <span style={{ color: "#a0aec0", fontWeight: "500" }}>---</span>;
                        }

                        const isRequired = value === true || String(value).toLowerCase() === 'yes' || String(value).toLowerCase() === 'true';
                        return isRequired ? (
                            <span style={{ fontWeight: "600", color: "#e53e3e", background: "#fff5f5", padding: "4px 10px", borderRadius: "6px" }}>
                                Yes
                            </span>
                        ) : (
                            <span style={{ fontWeight: "600", color: "#38a169", background: "#f0fff4", padding: "4px 10px", borderRadius: "6px" }}>
                                No
                            </span>
                        );
                    }
                },
                {
                    key: "DescriptionAndTimelines", label: "Description and Timelines", sortable: false, size: "220px",
                    render: ({ row, value }) => {
                        if (editingRowId === row.Id) {
                            return (
                                <textarea
                                    type="text"
                                    value={editData.DescriptionAndTimelines || ""}
                                    onChange={(e) => setEditData({ ...editData, DescriptionAndTimelines: e.target.value })}
                                    style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #cbd5e0", fontSize: "14px", width: "100%", boxSizing: "border-box" }}
                                    placeholder="Enter description..."
                                />
                            );
                        }

                        if (value === null || value === undefined || value === "") {
                            return <span style={{ color: "#a0aec0", fontWeight: "500" }}>---</span>;
                        }

                        return <span style={{ color: "#2d3748" }}>{value}</span>;
                    }
                },
                {
                    key: "Actions", label: "Actions", sortable: false, size: "190px",
                    render: ({ row }) => {
                        if (AppContext.userRole !== "Admin") {
                            return null;
                        }

                        if (editingRowId === row.Id) {
                            return (
                                <div style={{ display: "flex", gap: "6px" }}>
                                    {isSavingRow ? (
                                        <LoadingSpinnerImage />
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => saveRow(row)}
                                                style={{ padding: "4px 8px", background: "#3182ce", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", whiteSpace: "nowrap" }}
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={cancelEdit}
                                                style={{ padding: "4px 8px", background: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "bold", whiteSpace: "nowrap" }}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    )}
                                </div>
                            );
                        } else {
                            const isRequired = row.ActionRequired === true || String(row.ActionRequired).toLowerCase() === 'yes' || String(row.ActionRequired).toLowerCase() === 'true';

                            return (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <button
                                        onClick={() => startEdit(row)}
                                        style={{ padding: "4px 8px", background: "#edf2f7", color: "#4a5568", border: "1px solid #cbd5e0", borderRadius: "4px", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}
                                    >
                                        <i className="far fa-edit"></i> Edit
                                    </button>

                                    {/* Action Button: View Submission OR Create Submission */}
                                    {isRequired && (
                                        row.SubmissionId !== null && row.SubmissionId !== undefined ? (
                                            <a
                                                href={`/your-medboard/country-registrations/submissions/?submissionId=${row.SubmissionId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: "4px 8px",
                                                    background: "#48bb78",
                                                    color: "white",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    whiteSpace: "nowrap",
                                                    textDecoration: "none",
                                                    fontWeight: "500"
                                                }}
                                                title="View Submission"
                                            >
                                                <i className="fas fa-external-link-alt"></i> View Submission
                                            </a>
                                        ) : (
                                            <button
                                                onClick={() => handleCreateSubmission(row)}
                                                disabled={creatingSubmissionId === row.Id}
                                                style={{
                                                    padding: "4px 8px",
                                                    background: creatingSubmissionId === row.Id ? "#a0aec0" : "#3182ce",
                                                    color: "white",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    cursor: creatingSubmissionId === row.Id ? "not-allowed" : "pointer",
                                                    fontSize: "12px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px",
                                                    whiteSpace: "nowrap"
                                                }}
                                                title="Add To Submissions"
                                            >
                                                {creatingSubmissionId === row.Id ? (
                                                    <>
                                                        <i className="fas fa-spinner fa-spin"></i> Creating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-file-upload"></i> Create Submission
                                                    </>
                                                )}
                                            </button>
                                        )
                                    )}
                                </div>
                            );
                        }
                    },
                }
            ]}
            fetchUrl="/umbraco/api/RIMSRegistrations/ChangeAssessmentItems/"
            enableDelete={AppContext.userRole == "Admin" ? true : false}
            enableExport={false}
            deleteUrl="/umbraco/api/RIMSRegistrations/BulkChangeAssessmentItems/?itemsIds="
            exportUrl="/umbraco/api/RIMSRegistrations/ExportItems/?itemIds="
            queryParams={{ id: AppContext.currentItem.Id }}
            exportFileName={`${AppContext.currentItem.Title}-items-export.xlsx`}
            rowTitleKey="CertificateName"
            searchPlaceholder="Search Certificates"
            LoadingComponent={LoadingSpinnerImage}
            refresh={refresh + internalRefresh}
            showTotalCount={true}
            totalCountUrl="/umbraco/api/RIMSRegistrations/ChangeAssessmentItemsCount/"
            customButtons={[
                
                ...(AppContext.userRole === "Admin" && onAddProducts
                    ? [{
                        Name: "Add Products",
                        callback: onAddProducts,
                        cssIcon: "fas fa-plus-circle",
                        buttonBackground: "#3398dc",
                        buttonFontColor: "#f7f7f7",
                    }]
                    : []),{
                        Name: isFindingCertificates ? "Finding..." : "Find New Certificates",
                        callback: handleFindNewCertificates,
                        cssIcon: isFindingCertificates ? "fas fa-spinner fa-spin" : "fas fa-search",
                        buttonBackground: "#3398dc",
                        buttonFontColor: "#f7f7f7",
                    },
            ]}
        />
    </>
}

const SubmissionsTable = ({ AppContext, refresh, customButtons = null }) => {

    const rowStyleProcessor = (row) => {
        // Assuming there is a TargetDate or SubmissionDueDate field
        const value = row.TargetDate;
        if (!value) return "";

        const date = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);

        if (date < today && row.Status !== "Completed") return "datatable-row-red";

        const daysUntilDue = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
        if (daysUntilDue <= 30 && row.Status !== "Completed") return "datatable-row-yellow";

        return "";
    };

    return (
        <>
            <GenericDataTable
                columns={[
                    {
                        key: "IdCheckbox",
                        label: "",
                        sortable: false,
                        type: "checkbox",
                        size: "60px",
                    },
                    {
                        key: "Id", label: "Submission", sortable: false, size: "120px",
                        render: ({ value }) => (
                            <div style={{ textAlign: "center", width: "93px" }}>
                                <a
                                    href={`/your-medboard/country-registrations/submissions/?&submissionId=${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        backgroundColor: "#f7fafc", padding: "0px 6px", borderRadius: "5px", border: "1px solid #e2e8f0",
                                        color: "#3182ce",
                                        fontWeight: "500"
                                    }}
                                >
                                    Open ↗
                                </a>
                            </div>
                        ),
                    },
                    {
                        key: "Title", label: "Title", sortable: true, size: "300px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_Text value={value} />
                        ),
                    },
                    {
                        key: "CertificateName", label: "Certificate", sortable: true, size: "200px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_Text value={value} />
                        ),
                    },
                    {
                        key: "Status", label: "Status", sortable: true, size: "200px",
                        render: ({ row, value }) => (
                            <FieldTypes.FieldType_Submission_Status value={value} />
                        ),
                    },
                    {
                        key: "Priority", label: "Priority", sortable: true, size: "150px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_Submission_Priority value={value} />
                        ),
                    },
                    {
                        key: "AssignedTo", label: "Assignee", sortable: true, size: "150px",
                        render: ({ row, value }) => (
                            <FieldTypes.FieldType_User value={value} users={AppContext.users} />
                        ),
                    },
                    {
                        key: "ExpectedCompletionDate", label: "Expected Completion", sortable: true, size: "150px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_DateList value={value} />
                        ),
                    },
                    {
                        key: "Country", label: "Country", sortable: true, size: "150px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_Text value={value} />
                        ),
                    },
                    {
                        key: "DaysOpened", label: "Days Opened", sortable: true, size: "150px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_Text value={value} />
                        ),
                    },
                    {
                        key: "Type", label: "Type", sortable: true, size: "150px",
                        render: ({ value }) => (
                            <FieldTypes.FieldType_Submissions_Type value={value} />
                        ),
                    }
                ]}
                // You may need to adjust these API endpoints to match your actual routing for Submissions
                fetchUrl="/umbraco/api/Submission/ChangeAssessmentSubmissions/"
                enableDelete={AppContext.userRole == "Admin" ? true : false}
                enableExport={true}
                deleteUrl="/umbraco/api/Submission/Submissions/?id="
                exportUrl="/umbraco/api/Submission/SubmissionsExport/?submissionsIds="
                queryParams={{ "changeId": AppContext.currentItem.Id }}
                exportFileName={`${AppContext.currentItem.Name}-submissions-export.xlsx`}
                rowTitleKey="Title"
                searchPlaceholder="Search Submissions"
                LoadingComponent={LoadingSpinnerImage}
                customButtons={customButtons}
                refresh={refresh}
                showTotalCount={true}
                totalCountUrl="/umbraco/api/Submission/ChangeAssessmentSubmissionsCount/"
                rowStyleProcessor={rowStyleProcessor}
                initialSortColumn={"Title"}
            />
        </>
    );
};

export default ItemDetailsPane;